// src/pages/admin/Page_PlanesEduca/Page_PlanesEduca.js
//
// M07 - Pagina admin de Planes refactorizada con AntD + tema Educa360.
// - Cabecera con search + select cuenta + boton "Nuevo Plan".
// - Grid responsive de PlanCards (3 cols desktop / 2 tablet / 1 mobile).
// - Filtros 100% client-side. Backend trae todos los planes (limit alto)
//   y la pagina filtra + pagina en memoria.
// - Modal de crear/editar con PlanFormModal.
// - Borrado con Modal.confirm de antd + warning sobre activaciones.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Row, Col, Pagination, Empty, Modal, message, Button } from "antd";
import { PlusOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import { PageHeader, EducaThemeProvider } from "../../../ui";
import {
  PlanCard,
  PlanCardSkeleton,
  PlanFiltersBar,
  PlanFormModal,
} from "../../../components/Admin/PlanesEducaCmp/_shared";
import { PlanesAPI } from "../../../api";
import { useAuth } from "../../../hooks";
import "./Page_PlanesEduca.scss";

const planesController = new PlanesAPI();
const PAGE_SIZE_OPTIONS = ["12", "24", "48"];
const DEFAULT_PAGE_SIZE = 12;
// Pedimos un limit alto al backend y paginamos en cliente. Si el catalogo
// crece mucho (>300 planes), conviene mover los filtros al backend.
const SERVER_FETCH_LIMIT = 500;

export function Page_PlanesEduca() {
  const { accessToken, user } = useAuth();
  const idPermission = user?.user?._id || user?._id;

  const [allPlans, setAllPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [accountFilter, setAccountFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [formOpen, setFormOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  const [reloadFlag, setReloadFlag] = useState(0);
  const reload = useCallback(() => setReloadFlag((f) => f + 1), []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await planesController.getPlans(accessToken, {
          page: 1,
          limit: SERVER_FETCH_LIMIT,
        });
        if (alive) setAllPlans(response?.plans || []);
      } catch (e) {
        const msg =
          e?.body?.message || e?.message || "Error cargando los planes.";
        if (alive) {
          setError(msg);
          message.error(msg);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [accessToken, reloadFlag]);

  const filtered = useMemo(() => {
    const list = allPlans || [];
    const s = search.trim().toLowerCase();
    return list.filter((p) => {
      if (accountFilter !== "all" && p.account !== accountFilter) return false;
      if (!s) return true;
      const haystack = [p.title, p.description, p.vrHeadsetModel, p.bagModel]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase())
        .join(" ");
      return haystack.includes(s);
    });
  }, [allPlans, search, accountFilter]);

  // Reset a pagina 1 cuando cambia filtro/busqueda.
  useEffect(() => {
    setPage(1);
  }, [search, accountFilter]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const onCreate = () => {
    setEditingPlan(null);
    setFormOpen(true);
  };

  const onEdit = (plan) => {
    setEditingPlan(plan);
    setFormOpen(true);
  };

  const onDelete = useCallback(
    (plan) => {
      Modal.confirm({
        title: "Eliminar plan",
        icon: <ExclamationCircleFilled style={{ color: "#c63f3e" }} />,
        content: (
          <div>
            <p>
              Vas a eliminar el plan <strong>{plan?.title}</strong>. Esta accion
              no se puede deshacer.
            </p>
            <p style={{ fontSize: 12, color: "rgba(0,0,0,0.55)" }}>
              Si hay activaciones de partners asociadas a este plan, se quedaran
              apuntando a un plan inexistente. Verifica antes de eliminar.
            </p>
          </div>
        ),
        okText: "Eliminar",
        okButtonProps: { danger: true },
        cancelText: "Cancelar",
        onOk: async () => {
          try {
            if (!accessToken || !idPermission) {
              message.error("Sesion invalida. Vuelve a iniciar sesion.");
              return;
            }
            await planesController.deletePlan(
              accessToken,
              idPermission,
              plan._id,
            );
            message.success("Plan eliminado correctamente.");
            reload();
          } catch (e) {
            const msg =
              e?.body?.message || e?.message || "Error eliminando el plan.";
            message.error(msg);
          }
        },
      });
    },
    [accessToken, idPermission, reload],
  );

  return (
    <EducaThemeProvider>
      <div className="page-planes">
        <PageHeader
          title="Planes"
          subtitle="Catalogo de planes comerciales (Free / Premium / Promocion)"
          actions={
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <PlanFiltersBar
                search={search}
                onSearchChange={setSearch}
                accountFilter={accountFilter}
                onAccountFilterChange={setAccountFilter}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}>
                Nuevo Plan
              </Button>
            </div>
          }
        />

        {error ? (
          <div
            style={{
              padding: 12,
              background: "rgba(198,63,62,0.08)",
              color: "#c63f3e",
              borderRadius: 12,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        ) : null}

        <PlanesGrid
          loading={loading}
          plans={paged}
          isEmpty={!loading && filtered.length === 0}
          hasNoData={!loading && (allPlans || []).length === 0}
          onCreate={onCreate}
          onEdit={onEdit}
          onDelete={onDelete}
        />

        {!loading && filtered.length > 0 ? (
          <div className="planes-pagination">
            {filtered.length > pageSize ? (
              <Pagination
                current={page}
                total={filtered.length}
                pageSize={pageSize}
                showSizeChanger
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onChange={(p, ps) => {
                  setPage(p);
                  setPageSize(ps);
                }}
                showTotal={(total) =>
                  `${total} plan${total === 1 ? "" : "es"} en total`
                }
              />
            ) : (
              <span style={{ fontSize: 12, color: "rgba(0,0,0,0.5)" }}>
                {filtered.length} plan{filtered.length === 1 ? "" : "es"} en
                total
              </span>
            )}
          </div>
        ) : null}

        <PlanFormModal
          open={formOpen}
          plan={editingPlan}
          onClose={() => {
            setFormOpen(false);
            setEditingPlan(null);
          }}
          onSaved={reload}
        />
      </div>
    </EducaThemeProvider>
  );
}

function PlanesGrid({
  loading,
  plans,
  isEmpty,
  hasNoData,
  onCreate,
  onEdit,
  onDelete,
}) {
  if (loading) {
    return (
      <Row gutter={[20, 20]}>
        {Array.from({ length: 6 }).map((_, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Col key={`sk-${i}`} xs={24} sm={12} lg={8}>
            <PlanCardSkeleton />
          </Col>
        ))}
      </Row>
    );
  }
  if (hasNoData) {
    return (
      <Empty
        description="Aun no has creado ningun plan"
        style={{ padding: 60 }}
      >
        <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}>
          Crear primer plan
        </Button>
      </Empty>
    );
  }
  if (isEmpty) {
    return (
      <Empty
        description="Ningun plan coincide con tu busqueda. Prueba a cambiar el termino o el filtro."
        style={{ padding: 60 }}
      />
    );
  }
  return (
    <Row gutter={[20, 20]}>
      {plans.map((plan) => (
        <Col key={plan._id} xs={24} sm={12} lg={8}>
          <PlanCard plan={plan} onEdit={onEdit} onDelete={onDelete} />
        </Col>
      ))}
    </Row>
  );
}
